import type { AbstractIntlMessages } from 'next-intl';
import type { Locale, NamespacedMessages } from '../types';

/**
 * Recursively replace every string leaf with a mask.
 * Useful for debugging and spotting missing translations. 🧐
 * @returns A new JSON value with strings masked
 */
export function maskNamespacedMessages(
  /**
   * The namespaced messages to mask.
   */
  namespacedMessages: Record<string, AbstractIntlMessages>,
  {
    mask,
  }: {
    /**
     * The mask to use for strings.
     */
    mask?: string;
  } = {},
) {
  return Object.entries(namespacedMessages).reduce(
    (acc, [locale, messages]) => {
      acc[locale as Locale] = maskMessages(messages, { mask });
      return acc;
    },
    {} as NamespacedMessages<Locale>,
  );
}

type Json = string | number | boolean | null | JsonObject | Array<Json>;
interface JsonObject {
  [key: string]: Json;
}

/**
 * Recursively walks a JSON-like value and replaces every string leaf with a mask.
 * @returns A new JSON value with strings masked
 */
function maskMessages<T extends Json>(
  /**
   * JSON-compatible value.
   */
  input: T,
  {
    mask = '⚛️ ⚛️ ⚛️',
  }: {
    /**
     * The mask to use for strings.
     */
    mask?: string | undefined;
  } = {},
): T {
  if (typeof input === 'string') {
    return mask as T;
  }

  if (Array.isArray(input)) {
    return input.map((item) => maskMessages(item, { mask })) as T;
  }

  if (input !== null && typeof input === 'object') {
    const result: JsonObject = {};
    for (const [key, value] of Object.entries(input)) {
      result[key] = maskMessages(value, { mask });
    }
    return result as T;
  }
  return input;
}
